/**
 * 파일명 : evl0103m.tsx
 * 설명 : 최종 평가 결과 화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2026-01-20   변재석     최초작성
 * 1.1       2026-01-26   변재석     서버 계산 로직으로 변경 (프론트 계산 제거)
 * 1.2       2026-02-02   변재석     model/slice 분리 (타입, 상수, 유틸 함수)
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import { useState, useEffect, useRef } from "react";

/*  AntD  */
import {Table, Form, Input, Select, Space, Typography, Button, Tag, Row, Col, Modal, message, Timeline, Tooltip,} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import { DownloadOutlined, EditOutlined } from "@ant-design/icons";

/*  업무  */
import * as common from 'libs/common';
import * as commonProxy from 'libs/CommonProxy';
import Evl0102m from './evl0102m';
import Evl0105m from './evl0105m';
import Evl0106m from './evl0106m';
import Evl0107m from './evl0107m';

import * as XLSX from "xlsx";

/* Reducer - 타입 정의 */
import type { 
  IPropsEvl0103M, 
  IEvaluationRecord, 
  IProjectEvaluation,
  IEvaluationDetail,
  IScoreHistory,
} from '../../../redux/models/evl/evl0103m_model';

/* Reducer - Redux hooks */
import { useAppDispatch } from '../../../redux/hooks';
import { FrameAction } from '../../../redux/slice/frame/root_main_slice';

/* Reducer - 상수 및 유틸 함수 */
import { 
  GRADE_COLORS,
  GRADE_RANGES,
  getGradeFromScore,
  getGradeColor,
  calculatePositionTenure,
  calculateGradeStats,
  calculateScoreRangeStats,
  getScoreRangeKey,
} from '../../../redux/slice/evl/evl0103m_slice';

const { Title } = Typography;
const { Option } = Select;

// ============================================================================
// [참고] 점수 계산은 서버(EvlResultServiceImpl)에서 처리
// - HR 등급: S=1.0, A=0.9, B=0.8, C=0.7, D=0.6
// - PJ 등급: A=1.0, B=0.75, C=0.5, D=0.25
// ============================================================================

// 타입 별칭 (기존 코드 호환성을 위해)
type EvaluationRecord = IEvaluationRecord;
type ProjectEvaluation = IProjectEvaluation;
type EvaluationDetail = IEvaluationDetail;
type ScoreHistory = IScoreHistory;
type Evl0103mProps = IPropsEvl0103M;

/**
 * 컴포넌트명 : Evl0103m
 * 기능 : 최종 평가 결과 화면 메인 컴포넌트
 * 설명 : 평가년도별 평가 결과 조회 및 통계 표시
 **/ 
const Evl0103m: React.FC<Evl0103mProps> = (props) => {
  const currentYear = new Date().getFullYear().toString();
  const empNo = props.empNo || common.getEmpNo();
  // evlYear는 props에서 받지만 화면에서 selectedYear로 관리

  // Redux dispatch (evl0107m activeProps 설정용)
  const dispatch = useAppDispatch();

  // HR 관리자 여부 확인
  const isHrAdmin = common.isHrAdmin();

  // 상태 관리
  const [evaluationData, setEvaluationData] = useState<EvaluationRecord[]>([]);
  const [filteredData, setFilteredData] = useState<EvaluationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [evaluationYears, setEvaluationYears] = useState<any[]>([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [currentPage, setCurrentPage] = useState(1);  // 테이블 페이지 상태
  
  // 등급별 목표 비율 (서버에서 조회 - 엑셀 등에서 참조용)
  const [gradeWeights, setGradeWeights] = useState<{
    S: number; A: number; B: number; C: number; D: number;
  }>({ S: 0, A: 0, B: 0, C: 0, D: 0 });
  
  // HR 평가 시작일 (인사평가 버튼 활성화 여부 판단용)
  const [hrEvlStDt, setHrEvlStDt] = useState<string>('');
  
  // 평가기간 구분 (HR: 인사평가기간, PLG: 업무서약기간)
  const [period, setPeriod] = useState<string>('');
  
  // 임원 여부 (본부장/사업부장/CEO - 직책코드 04/05/09)
  const [isExecutive, setIsExecutive] = useState(false);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<EvaluationRecord>>({});  // 테이블 정렬 상태
  
  // 최종점수 수정 모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<EvaluationRecord | null>(null);
  const [newScore, setNewScore] = useState<string>("0");
  const [scoreReason, setScoreReason] = useState<string>("");
  const scoreReasonRef = useRef<string>("");  // 입력 버벅거림 방지용 ref
  const [scoreReasonCount, setScoreReasonCount] = useState<number>(0);
  const [resetKey, setResetKey] = useState<number>(0);  // TextArea 초기화용 key
  const [isResetMode, setIsResetMode] = useState<boolean>(false);  // 초기화 모드 여부
  const positionOrder: { [key: string]: number } = {     // 직급 순서: 상무이사 > 이사 > 부장 > 차장 > 과장 > 주임
          "상무이사": 6,
          "이사": 5,
          "부장": 4,
          "차장": 3,
          "과장": 2,
          "주임": 1,
        };

  // 업무서약서 조회 모달 상태
  const [isEvl0102mOpen, setIsEvl0102mOpen] = useState(false);
  const [selectedEvl0102mRecord, setSelectedEvl0102mRecord] = useState<EvaluationRecord | null>(null);

  // 인사평가(HR) 조회 모달 상태
  const [isEvl0105mOpen, setIsEvl0105mOpen] = useState(false);
  const [selectedEvl0105mRecord, setSelectedEvl0105mRecord] = useState<EvaluationRecord | null>(null);

  // 프로젝트평가 조회 모달 상태 (evl0106m: 팀원, evl0107m: PM)
  const [isEvl0106mOpen, setIsEvl0106mOpen] = useState(false);
  const [selectedEvl0106mRecord, setSelectedEvl0106mRecord] = useState<{empNo: string; evlId: string; record: EvaluationRecord; project: ProjectEvaluation} | null>(null);
  const [isEvl0107mOpen, setIsEvl0107mOpen] = useState(false);
  const [selectedEvl0107mRecord, setSelectedEvl0107mRecord] = useState<{empNo: string; evlId: string; record: EvaluationRecord; project: ProjectEvaluation} | null>(null);

  // 프로젝트평가 모달 열기 함수
  const openProjectEvalModal = (record: EvaluationRecord, project: ProjectEvaluation) => {
    const params = {
      empNo: record.empNo,
      evlId: project.pjMapId,
      record: record,
      project: project,
    };
    
    if (project.evlCd === '03') {
      // PM 평가 화면 (evl0107m) - Redux activeProps 설정 필요
      dispatch(FrameAction.setActiveProps({
        evlId: project.pjMapId,
        empNo: record.empNo,
        evlType: '03',
        hrMapId: record.hrMapId,
        pjNm: project.projectName,
        pjCd: '',
        evlYear: record.evaluationYear,
        evlEnableYn: 'N',
      }));
      setSelectedEvl0107mRecord(params);
      setIsEvl0107mOpen(true);
    } else if (project.evlCd === '04') {
      // 팀원 평가 화면 (evl0106m)
      setSelectedEvl0106mRecord(params);
      setIsEvl0106mOpen(true);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchEvaluationYears();
  }, []);

  // 등급 필터 변경 시 적용
  useEffect(() => {
    applyGradeFilter();
  }, [selectedGrades, evaluationData]);

  /**
   * 기능 : fetchEvaluationYears
   * 설명 : 평가년도 목록 조회 API 호출 (TREVLR0016)
   *        ✅ 당해년도가 목록에 있으면 자동 선택 및 조회
   **/
  const fetchEvaluationYears = () => {
    const requestData = {};

    commonProxy.callAsyncSvc(requestData, {
      txCd: 'TREVLR0016',
      success: (responseData: any) => {
        const data = responseData.data || responseData;
        const evlYears = data.evlYears || [];
        
        if (Array.isArray(evlYears) && evlYears.length > 0) {
          setEvaluationYears(evlYears);
          
          // ✅ 당해년도 자동 선택 로직 추가
          const thisYear = String(new Date().getFullYear());
          
          // 올해가 목록에 있는지 확인
          const hasThisYear = evlYears.some(
            (y: any) => y.evlYear === thisYear || y.value === thisYear
          );
          
          if (hasThisYear) {
            // 자동 조회 시 이전에 남아있을 수 있는 검색 필드(직급 등)를 완전 초기화
            form.resetFields();
            form.setFieldsValue({ evaluationYear: thisYear, department: "", position: "" });

            // 초기화된 상태로 자동 조회 실행
            fetchEvaluationData({ evaluationYear: thisYear }); // 🔥 자동 조회까지 실행
          }
        }
      },
      error: (errorMsg: string) => {
        messageApi.error(errorMsg || '평가년도 조회에 실패했습니다.');
      }
    });
  };

  /**
   * 기능 : fetchEvaluationData
   * 설명 : 평가 데이터 조회 API 호출 (TREVLR0018)
   *        서버에서 계산된 점수를 그대로 사용
   **/
  const fetchEvaluationData = (searchParams?: {
    evaluationYear?: string;
    evaluateeName?: string;
    evaluatorName?: string;
    position?: string;
    department?: string;
  }, preserveFilters?: boolean) => {
    setLoading(true);
    
    const requestData = {
      empNo: empNo,
      evlYear: searchParams?.evaluationYear || '',
      evaluateeName: searchParams?.evaluateeName || '',
      evaluatorName: searchParams?.evaluatorName || '',
      position: searchParams?.position || '',
      department: searchParams?.department || '',
      //isHrAdmin: isHrAdmin ? 1 : 0,
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: 'TREVLR0018',
      success: (responseData: any) => {
        try {
          const data = responseData.data || responseData;
          const list = data.list || [];
          
          // 서버에서 계산된 데이터를 그대로 매핑
          const records: EvaluationRecord[] = list.map((item: any, index: number) => ({
            key: `${item.empNo}_${index}`,  // 중복 방지를 위해 index 추가
            empNo: item.empNo || '',  // 사원번호 별도 저장
            hrMapId: item.hrMapId || '',  // 인사평가 매핑ID
            evaluateeName: item.userNm || '',
            evaluatorName: item.evaluatorName || '',
            department: item.deptNm || '',
            position: item.postNm || '',
            positionTenure: calculatePositionTenure(item.currPosStDt),
            evaluationYear: item.evlYear || '',
            finalScore: item.fnlScr || 0,
            scrAdjRsn: item.scrAdjRsn || '',
            eval2SubYn: item.eval2SubYn || 'N',  // 2차평가 제출여부
            slfSubYn: item.slfSubYn || 'N',        // 자기평가 제출여부
            eval1SubYn: item.eval1SubYn || 'N',    // 1차평가 제출여부
            bizPlgSubYn: item.bizPlgSubYn || 'N',  // 업무서약서 제출여부
            finalScoreHistory: [] as ScoreHistory[],
            selfEvaluation: {
              performance: item.selfPerf || 0,
              commonCompetency: item.selfCommon || 0,
              roleCompetency: item.selfRole || 0,
              jobCompetency: item.selfJob || 0,
              total: item.selfTotal || 0,
              comment: '',
            },
            projectEvaluation: { 
              common: 0,
              jobSpecific: 0,
              total: 0,
              comment: '',
            },
            firstEvaluation: {
              performance: item.evl1Perf || 0,
              commonCompetency: item.evl1Common || 0,
              roleCompetency: item.evl1Role || 0,
              jobCompetency: item.evl1Job || 0,
              total: item.evl1Total || 0,
              comment: item.evl1Rmk || '',
            },
            secondEvaluation: {
              performance: item.evl2Perf || 0,
              commonCompetency: item.evl2Common || 0,
              roleCompetency: item.evl2Role || 0,
              jobCompetency: item.evl2Job || 0,
              total: item.evl2Total || 0,
              comment: item.evl2Rmk || '',
            },
            projectEvaluations: (item.projects || []).map((p: any, idx: number) => ({
              key: `${p.pjMapId}_${p.evlCd}_${idx}`,
              pjMapId: p.pjMapId || '',
              evlCd: p.evlCd || '',
              pmSubYn: p.pmSubYn || 'N',  // 프로젝트평가 제출여부
              projectName: p.projectNm || '',
              grade: '',
              description: '',
              period: '',
              common: p.commonSum || 0,
              jobSpecific: p.jobSum || 0,
              total: p.totalSum || 0,
              comment: p.pjEvlRmk || ''
            })),
            isEval1: item.isEval1 || 'N',
            isEval2: item.isEval2 || 'N',
            canViewSecondEval: item.canViewSecondEval || 'N',
          }));

          setEvaluationData(records);
          if (!preserveFilters) {
            setFilteredData(records);
            // 데이터 로드 완료 후에도 폼의 직급 필드가 남아있을 수 있으므로 명시적으로 초기화
            form.setFieldsValue({ department: "", position: "" });
          }
          // preserveFilters=true인 경우 useEffect[selectedGrades, evaluationData]가
          // applyGradeFilter()를 실행하여 기존 폼값+등급필터 기준으로 filteredData를 자동 갱신
          
          // 등급별 목표 비율 설정 (서버에서 조회한 값)
          setGradeWeights({
            S: data.sGrdWt ?? 5,
            A: data.aGrdWt ?? 20,
            B: data.bGrdWt ?? 50,
            C: data.cGrdWt ?? 20,
            D: data.dGrdWt ?? 5,
          });
          
          // HR 평가 시작일 설정 (인사평가 버튼 활성화 여부)
          setHrEvlStDt(data.hrEvlStDt || '');
          
          // 평가기간 구분 설정
          setPeriod(data.period || 'PLG');
          
          // 임원 여부 설정 (본부장/사업부장/CEO)
          setIsExecutive(data.isExecutive === true || data.isExecutive === 'Y');
          
          setLoading(false);
          
          if (records.length === 0) {
            messageApi.info('조회된 데이터가 없습니다.');
          } else {
            messageApi.success("조회되었습니다.");
          }
        } catch (error) {
          console.error('Data processing error:', error);
          setLoading(false);
          messageApi.error('데이터 처리 중 오류가 발생했습니다.');
        }
      },
      error: (errorMsg: string) => {
        setLoading(false);
        messageApi.error(errorMsg || '데이터 조회에 실패했습니다.');
      }
    });
  };
  
  // 수정 모달 열기
  const openScoreModal = (record: EvaluationRecord) => {
    setEditingRecord(record);
    setNewScore(String(record.finalScore));
    setScoreReason(record.scrAdjRsn || '');
    scoreReasonRef.current = record.scrAdjRsn || '';  // ref도 초기화
    setScoreReasonCount((record.scrAdjRsn || '').length);
    setIsModalOpen(true);
  };
  
  // 수정 모달 닫기
  const closeScoreModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    setNewScore("0");
    setScoreReason("");
    scoreReasonRef.current = "";  // ref도 초기화
    setScoreReasonCount(0);
    setIsResetMode(false);
  };

  // 수정 모달 초기화 (2차평가 점수로 복원, 사유 비움)
  const resetScoreModal = () => {
    if (!editingRecord) return;
    const evl2Score = editingRecord.secondEvaluation?.total ?? 0;
    Modal.confirm({
      title: '초기화',
      content: `최종점수가 2차평가 점수(${evl2Score}점)로 복원되고, 수정사유가 비워집니다. 초기화가 즉시 반영됩니다.`,
      okText: '초기화',
      cancelText: '취소',
      onOk: () => {
        setNewScore(String(evl2Score));
        setScoreReason("");
        scoreReasonRef.current = "";
        setScoreReasonCount(0);
        setResetKey(prev => prev + 1);
        setIsResetMode(true);

        const requestData = {
          empNo: editingRecord.empNo,
          evlYear: editingRecord.evaluationYear,
          fnlScr: evl2Score,
          scrAdjRsn: '',
          reset: true,
        };
        commonProxy.callAsyncSvc(requestData, {
          txCd: 'TREVLS0018',
          success: (responseData: any) => {
            const data = responseData.data || responseData;
            if (data.resultCode === "400") {
              message.error(data.resultMsg || '초기화에 실패했습니다.');
              return;
            }
            message.success("최종점수가 수정되었습니다.");
            closeScoreModal();
            fetchEvaluationData({ evaluationYear: editingRecord.evaluationYear }, true);
          },
          error: (errorMsg: string) => {
            message.error(errorMsg || '초기화에 실패했습니다.');
          },
        });
      },
    });
  };
  
  // 업무서약서 모달 열기
  const openEvl0102m = (record: EvaluationRecord) => {
    setSelectedEvl0102mRecord(record);
    setIsEvl0102mOpen(true);
  };

  // 인사평가(HR) 모달 열기
  const openEvl0105m = (record: EvaluationRecord) => {
    setSelectedEvl0105mRecord(record);
    setIsEvl0105mOpen(true);
  };
  
  // 최종점수 수정 저장
  const handleSaveScore = async () => {
    if (!editingRecord) return;
    
    const reason = scoreReasonRef.current;  // ref에서 값 가져오기
    const numScoreForCheck = parseFloat(newScore || "0");
    
    // 초기화 모드가 아닌 경우에만 수정 사유 필수
    if (!isResetMode && numScoreForCheck > 0 && !reason.trim()) {
      message.error("수정 사유를 입력해주세요.");
      return;
    }

    // 새로운 점수 유효성 검사 (decimal 5,2 형식: 정수 3자리, 소수점 2자리)
    const scoreDecimalRegex = /^\d{1,3}(\.\d{1,2})?$/;
    if (!scoreDecimalRegex.test(newScore)) {
      message.error("점수는 100.00 이하의 소수점 2자리까지만 입력 가능합니다.");
      return;
    }

    const numScore = parseFloat(newScore);
    // 점수 범위 검증 (0 ~ 100.00)
    if (isNaN(numScore) || numScore < 0 || numScore > 100.00) {
      message.error("점수는 0 이상 100.00 이하여야 합니다.");
      return;
    }

    // TREVLS0018 호출 - 최종점수 수정 (점수 미변경 시 사유만 수정 가능)
    const requestData = {
      empNo: editingRecord.empNo,  // key가 아닌 empNo 사용
      evlYear: editingRecord.evaluationYear,
      fnlScr: parseFloat(newScore),
      scrAdjRsn: isResetMode ? '' : reason,  // 초기화 시 사유 비움
      reset: isResetMode,  // 초기화 여부 플래그
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: 'TREVLS0018',
      success: (responseData: any) => {
        const data = responseData.data || responseData;
        const resultCode = data.resultCode;
        const resultMsg = data.resultMsg;
        
        if (resultCode === "400") {
          message.error(resultMsg || '최종점수 수정에 실패했습니다.');
          return;
        }
        
        message.success("최종점수가 수정되었습니다.");
        closeScoreModal();
        
        // TREVLR0018 다시 호출 - 업데이트된 데이터 조회 (기존 검색 필터 유지)
        fetchEvaluationData({ evaluationYear: editingRecord.evaluationYear }, true);
      },
      error: (errorMsg: string) => {
        message.error(errorMsg || '최종점수 수정에 실패했습니다.');
      },
    });
  };

  // 대시보드용 통계: 검색 필터(부서/직급/이름)만 적용, 점수 카드 필터 제외
  // → 점수 카드를 클릭해도 대시보드 인원수는 변하지 않음
  const getDashboardData = () => {
    let filtered = evaluationData;
    const formValues = form.getFieldsValue();
    if (formValues.evaluateeName) {
      filtered = filtered.filter(item => item.evaluateeName.includes(formValues.evaluateeName));
    }
    if (formValues.department) {
      filtered = filtered.filter(item => item.department.includes(formValues.department));
    }
    if (formValues.position) {
      filtered = filtered.filter(item => item.position === formValues.position);
    }
    return filtered;
  };
  const dashboardData = getDashboardData();
  const totalCount = dashboardData.length;

  // 점수 구간별 통계 계산 (검색 필터 기준, 점수 카드 필터 제외)
  const scoreRangeStats = calculateScoreRangeStats(dashboardData);

  // 등급/점수구간 필터 적용 (검색은 서버에서 처리됨)
  const applyGradeFilter = () => {
    let filtered = evaluationData;

    // 점수 구간 필터 적용
    if (selectedGrades.length > 0) {
      filtered = filtered.filter((item) => {
        const rangeKey = getScoreRangeKey(item);
        return selectedGrades.includes(rangeKey);
      });
    }

    // 검색 필터도 함께 적용
    const formValues = form.getFieldsValue();
    if (formValues.evaluateeName) {
      filtered = filtered.filter(item => item.evaluateeName.includes(formValues.evaluateeName));
    }
    if (formValues.department) {
      filtered = filtered.filter(item => item.department.includes(formValues.department));
    }
    if (formValues.position) {
      filtered = filtered.filter(item => item.position === formValues.position);
    }

    // 점수 구간 필터가 적용된 경우 점수 내림차순 정렬
    if (selectedGrades.length > 0) {
      filtered = [...filtered].sort((a, b) => (b.finalScore || 0) - (a.finalScore || 0));
    }

    setFilteredData(filtered);
  };

  // 점수 구간 카드 클릭 핸들러
  const handleGradeClick = (rangeKey: string) => {
    if (selectedGrades.includes(rangeKey)) {
      setSelectedGrades(selectedGrades.filter((g) => g !== rangeKey));
    } else {
      setSelectedGrades([...selectedGrades, rangeKey]);
    }
  };

  /**
   * 기능 : handleYearChange
   * 설명 : 평가년도 드롭다운 변경 시 (연도 선택/해제만 처리, 데이터 조회는 검색 버튼에서)
   * param : year - 선택된 평가년도
   **/
  const handleYearChange = (year: string) => {
    // 연도만 변경, 데이터는 초기화하지 않음
  };

  /**
   * 기능 : applyLocalFilter
   * 설명 : 이미 받은 데이터에서 로컬 필터링만 수행
   * param : filterParams - 필터 조건
   **/
  const applyLocalFilter = (filterParams: {
    evaluateeName?: string;
    evaluatorName?: string;
    position?: string;
    department?: string;
  }) => {
    let filtered = evaluationData;

    // 점수 구간 필터 먼저 적용
    if (selectedGrades.length > 0) {
      filtered = filtered.filter((item) => {
        const rangeKey = getScoreRangeKey(item);
        return selectedGrades.includes(rangeKey);
      });
    }

    // 검색 필터 적용
    if (filterParams.evaluateeName) {
      filtered = filtered.filter(item =>
        item.evaluateeName.includes(filterParams.evaluateeName!)
      );
    }
    if (filterParams.evaluatorName) {
      filtered = filtered.filter(item =>
        item.evaluatorName.includes(filterParams.evaluatorName!)
      );
    }
    if (filterParams.position) {
      filtered = filtered.filter(item => item.position === filterParams.position);
    }
    if (filterParams.department) {
      filtered = filtered.filter(item =>
        item.department.includes(filterParams.department!)
      );
    }

    setFilteredData(filtered);
  };

  /**
   * 기능 : handleSearch
   * 설명 : 검색 버튼 클릭 시 실행되는 함수
   * param : values - 폼 입력값
   **/
  const handleSearch = (values: any) => {
    const year = values.evaluationYear;

    // 년도 미선택 시 경고
    if (!year) {
      messageApi.warning("검색년도를 입력하십시오");
      return;
    }

    // 이미 해당 연도 데이터가 로드되어 있으면 로컬 필터만 적용
    if (evaluationData.length > 0 && evaluationData.some((r) => r.evaluationYear === year)) {
      applyLocalFilter({
        evaluateeName: values.evaluateeName,
        evaluatorName: values.evaluatorName,
        position: values.position,
        department: values.department,
      });
      return;
    }

    // 데이터가 없으면 서버에서 조회
    setSelectedGrades([]);
    fetchEvaluationData({ evaluationYear: year });
  };

  /**
   * 기능 : handleReset
   * 설명 : 초기화 버튼 클릭 시 검색 조건 초기화 및 전체 데이터 복원
   **/
  const handleReset = () => {
    const currentYear = form.getFieldValue('evaluationYear');  // 연도 보존
    form.resetFields();
    form.setFieldsValue({ evaluationYear: currentYear, department: "", position: "" });
    setSelectedGrades([]);
    setFilteredData(evaluationData);
    setCurrentPage(1);  // 1페이지로 이동
    setSortedInfo({});   // 테이블 정렬 초기화
    messageApi.info("필터가 초기화되었습니다.");
  };

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    if (filteredData.length === 0) {
      message.warning('다운로드할 데이터가 없습니다.');
      return;
    }

    try {
      // 워크북 생성
      const wb = XLSX.utils.book_new();
      
      // 헤더 행 구성
      const headerRow1 = [
        '이름', '최종점수', '부서', '직급', '직급 근무기간',
        '자기평가', '', '', '', '',
        '프로젝트평가', '', '', '', '',
        '1차평가', '', '', '', '', '',
        '2차평가', '', '', '', '', ''
      ];
      
      const headerRow2 = [
        '', '', '', '', '',
        '업적평가', '공통역량', '역할역량', '직무역량', '계',
        '프로젝트명', '공통', '직무별', '계', '평가의견',
        '업적평가', '공통역량', '역할역량', '직무역량', '계', '평가의견',
        '업적평가', '공통역량', '역할역량', '직무역량', '계', '평가의견'
      ];

      // 데이터 행 생성
      const dataRows: any[] = [];
      
      filteredData.forEach((record) => {
        const projectCount = record.projectEvaluations.length;
        const maxRows = Math.max(1, projectCount); // 최소 1행

        for (let i = 0; i < maxRows; i++) {
          const project = record.projectEvaluations[i];
          
          // 최종점수는 관리자만, 2차평가는 관리자+2차평가자/임원
          const canViewFinal = isHrAdmin;
          const canView2nd = isHrAdmin || record.canViewSecondEval === 'Y';

          const row = [
            // 첫 번째 프로젝트 행에만 기본 정보 표시
            i === 0 ? record.evaluateeName : '',
            i === 0 ? (canViewFinal ? record.finalScore : '-') : '',
            i === 0 ? record.department : '',
            i === 0 ? record.position : '',
            i === 0 ? record.positionTenure : '',
            // 자기평가
            i === 0 ? record.selfEvaluation.performance : '',
            i === 0 ? record.selfEvaluation.commonCompetency : '',
            i === 0 ? record.selfEvaluation.roleCompetency : '',
            i === 0 ? record.selfEvaluation.jobCompetency : '',
            i === 0 ? record.selfEvaluation.total : '',
            // 프로젝트평가
            project ? project.projectName : '',
            project ? project.common : '',
            project ? project.jobSpecific : '',
            project ? project.total : '',
            project && project.pmSubYn === 'Y' ? project.comment : '',
            // 1차평가
            i === 0 ? record.firstEvaluation.performance : '',
            i === 0 ? record.firstEvaluation.commonCompetency : '',
            i === 0 ? record.firstEvaluation.roleCompetency : '',
            i === 0 ? record.firstEvaluation.jobCompetency : '',
            i === 0 ? record.firstEvaluation.total : '',
            i === 0 && record.eval1SubYn === 'Y' ? record.firstEvaluation.comment : '',
            // 2차평가 (관리자 또는 2차평가자만 열람)
            i === 0 ? (canView2nd ? record.secondEvaluation.performance : '-') : '',
            i === 0 ? (canView2nd ? record.secondEvaluation.commonCompetency : '-') : '',
            i === 0 ? (canView2nd ? record.secondEvaluation.roleCompetency : '-') : '',
            i === 0 ? (canView2nd ? record.secondEvaluation.jobCompetency : '-') : '',
            i === 0 ? (canView2nd ? record.secondEvaluation.total : '-') : '',
            i === 0 && canView2nd && record.eval2SubYn === 'Y' ? record.secondEvaluation.comment : ''
          ];
          
          dataRows.push(row);
        }
      });

      // 워크시트 데이터 생성
      const wsData = [headerRow1, headerRow2, ...dataRows];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      // 컬럼 너비 설정
      ws['!cols'] = [
        { wch: 10 },  // 이름
        { wch: 10 },  // 최종점수
        { wch: 12 },  // 부서
        { wch: 8 },   // 직급
        { wch: 14 },  // 직급 근무기간
        // 자기평가
        { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 8 },
        // 프로젝트평가
        { wch: 20 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 30 },
        // 1차평가
        { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 8 }, { wch: 30 },
        // 2차평가
        { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 8 }, { wch: 30 },
      ];

      // 병합 셀 설정
      const merges: XLSX.Range[] = [
        // 1행 병합 (대분류 헤더)
        { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },  // 이름
        { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },  // 최종점수
        { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } },  // 부서
        { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } },  // 직급
        { s: { r: 0, c: 4 }, e: { r: 1, c: 4 } },  // 직급 근무기간
        { s: { r: 0, c: 5 }, e: { r: 0, c: 9 } },   // 자기평가
        { s: { r: 0, c: 10 }, e: { r: 0, c: 14 } }, // 프로젝트평가
        { s: { r: 0, c: 15 }, e: { r: 0, c: 20 } }, // 1차평가
        { s: { r: 0, c: 21 }, e: { r: 0, c: 26 } }, // 2차평가
      ];

      // 데이터 행의 병합 처리 (프로젝트가 여러 개인 경우)
      let currentRow = 2; // 데이터는 3행부터 시작 (0-based index)
      filteredData.forEach((record) => {
        const projectCount = record.projectEvaluations.length;
        const maxRows = Math.max(1, projectCount);

        if (maxRows > 1) {
          // 기본 정보 컬럼 병합 (이름, 최종점수, 부서, 직급, 직급 근무기간)
          for (let col = 0; col < 5; col++) {
            merges.push({ s: { r: currentRow, c: col }, e: { r: currentRow + maxRows - 1, c: col } });
          }
          // 자기평가 컬럼 병합
          for (let col = 5; col < 10; col++) {
            merges.push({ s: { r: currentRow, c: col }, e: { r: currentRow + maxRows - 1, c: col } });
          }
          // 1차평가 컬럼 병합
          for (let col = 15; col < 21; col++) {
            merges.push({ s: { r: currentRow, c: col }, e: { r: currentRow + maxRows - 1, c: col } });
          }
          // 2차평가 컬럼 병합
          for (let col = 21; col < 27; col++) {
            merges.push({ s: { r: currentRow, c: col }, e: { r: currentRow + maxRows - 1, c: col } });
          }
        }

        currentRow += maxRows;
      });

      ws['!merges'] = merges;

      // 워크북에 워크시트 추가
      XLSX.utils.book_append_sheet(wb, ws, '최종 평가 결과');

      // 파일 다운로드
      const date = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `최종_평가_결과_${date}.xlsx`);
      
      message.success('엑셀 파일이 다운로드되었습니다.');
    } catch (error) {
      message.error('엑셀 다운로드 중 오류가 발생했습니다.');
      console.error('Excel download error:', error);
    }
  };

  // 프로젝트 평가 상세 테이블 컬럼 (현재 미사용 - 확장테이블에서 사용 예정)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const projectColumns: ColumnsType<ProjectEvaluation> = [
    {
      title: "프로젝트명",
      dataIndex: "projectName",
      key: "projectName",
      width: 200,
    },
    {
      title: "기간",
      dataIndex: "period",
      key: "period",
      width: 150,
    },
    {
      title: "설명",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "등급",
      dataIndex: "grade",
      key: "grade",
      width: 100,
      align: "center" as const,
      render: (grade: string) => (
        <Tag
          color={getGradeColor(grade)}
          style={{ fontSize: "14px", fontWeight: "bold" }}
        >
          {grade}
        </Tag>
      ),
    },
  ];

  /**
   * 컬럼 표시 조건:
   * - 관리자(isHrAdmin): 모든 컬럼 표시, 최종점수 수정 가능
   * - 2차평가자 또는 임원(canViewSecondEval='Y'): 2차평가까지 표시 (최종점수 숨김)
   * - 1차평가자(isEval1='Y'): 1차평가까지만 표시 (2차평가, 최종점수 숨김)
   */
  
  // 메인 테이블 컬럼 - 엑셀 형식에 맞춰 상세하게 구성
  const columns: ColumnsType<EvaluationRecord> = [
    {
      title: "이름",
      dataIndex: "evaluateeName",
      key: "evaluateeName",
      width: 80,
      align: "center" as const,
      fixed: "left" as const,
    },
    {
      title: "업무서약서",
      key: "workPledge",
      width: 100,
      align: "center" as const,
      fixed: "left" as const,
      render: (_, record) => {
        const canOpen = record.bizPlgSubYn === 'Y';
        return (
          <Button
            type="link"
            onClick={() => canOpen && openEvl0102m(record)}
            style={{ fontWeight: "500", color: canOpen ? undefined : "#999", cursor: canOpen ? "pointer" : "default" }}
          >
            보기
          </Button>
        );
      },
    },
    {
      title: "인사평가",
      key: "hrEvaluation",
      width: 100,
      align: "center" as const,
      fixed: "left" as const,
      render: (_, record) => {
        // HR 기간이 아니거나 hrMapId가 없으면 비활성화
        if (period !== 'HR' || !record.hrMapId) {
          return (
            <Button type="link" style={{ fontWeight: "500", color: "#999", cursor: "default" }}>
              보기
            </Button>
          );
        }
        // 관리자: 2차평가 제출완료 시 열람
        // 임원+1차평가자: 자기평가 제출완료 시 열람
        // 임원+2차평가자: 1차평가 제출완료 시 열람
        // 임원(비1차,비2차): 2차평가 제출완료 시 열람
        // 2차평가자: 1차평가 제출완료 시 열람
        // 1차평가자: 자기평가 제출완료 시 열람
        let canOpen = false;
        if (isHrAdmin) {
          canOpen = record.eval2SubYn === 'Y';
        } else if (isExecutive && record.isEval1 === 'Y') {
          canOpen = record.slfSubYn === 'Y';
        } else if (isExecutive && record.isEval2 === 'Y') {
          canOpen = record.eval1SubYn === 'Y';
        } else if (isExecutive) {
          canOpen = record.eval2SubYn === 'Y';
        } else if (record.isEval2 === 'Y') {
          canOpen = record.eval1SubYn === 'Y';
        } else if (record.isEval1 === 'Y') {
          canOpen = record.slfSubYn === 'Y';
        }
        return (
          <Button
            type="link"
            onClick={() => canOpen && openEvl0105m(record)}
            style={{ fontWeight: "500", color: canOpen ? undefined : "#999", cursor: canOpen ? "pointer" : "default" }}
          >
            보기
          </Button>
        );
      },
    },
    {
      title: "최종점수",
      dataIndex: "finalScore",
      key: "finalScore",
      width: 120,
      align: "center" as const,
      fixed: "left" as const,
      sorter: (a, b) => (a.finalScore || 0) - (b.finalScore || 0),
      sortOrder: sortedInfo.columnKey === 'finalScore' ? sortedInfo.order : null,
      render: (value, record) => {
        // 최종점수는 관리자만 열람/수정 가능
        const canView = isHrAdmin;
        // 수정 기한: 평가년도 12/31 23:59:59까지
        const isExpired = new Date() > new Date(`${record.evaluationYear}-12-31T23:59:59`);
        const canEdit = isHrAdmin && !isExpired;
        
        if (!canView) {
          return <span style={{ fontWeight: "bold", fontSize: "16px", color: "#999" }}>-</span>;
        }
        
        // 버튼 활성화 조건: 수정 가능 + 2차평가 제출완료
        const isReady = canEdit && record.eval2SubYn === 'Y';
        // 버튼 문구: 기한 만료 > 2차 미제출 > 수정 가능
        const btnLabel = isExpired ? '마감' : (record.eval2SubYn === 'Y' ? '수정' : '진행중');
        const btnTitle = isExpired ? `수정 기한 만료 (${record.evaluationYear}-12-31)` : (record.eval2SubYn !== 'Y' ? '2차평가 미제출' : '');
        
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            <span style={{ fontWeight: "bold", fontSize: "16px" }}>
              {value}
            </span>
            {isHrAdmin && (
              <Button
                type={isReady ? "primary" : "default"}
                size="small"
                icon={isReady ? <EditOutlined /> : undefined}
                onClick={() => isReady && openScoreModal(record)}
                style={{ 
                  fontSize: "12px",
                  color: !isReady ? "#999" : undefined,
                  cursor: !isReady ? "default" : "pointer",
                }}
                disabled={!isReady}
                title={btnTitle}
              >
                {btnLabel}
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: "부서",
      dataIndex: "department",
      key: "department",
      width: 140,
      align: "center" as const,
      fixed: "left" as const,
      sorter: (a, b) => a.department.localeCompare(b.department),
      sortOrder: sortedInfo.columnKey === 'department' ? sortedInfo.order : null,
    },
    {
      title: "직급",
      dataIndex: "position",
      key: "position",
      width: 80,
      align: "center" as const,
      fixed: "left" as const,
      sorter: (a, b) => {

        return (positionOrder[a.position] || 0) - (positionOrder[b.position] || 0);
      },
      sortOrder: sortedInfo.columnKey === 'position' ? sortedInfo.order : null,
      //defaultSortOrder: 'descend' as const,  // 기본 내림차순 정렬 (상무이사가 위로)
    },
    {
      title: "직급 근무기간",
      dataIndex: "positionTenure",
      key: "positionTenure",
      width: 110,
      align: "center" as const,
      fixed: "left" as const,
    },
    {
      title: "자기평가",
      className: "col-self-eval",
      children: [
        {
          title: "업적평가",
          key: "selfPerformance",
          width: 90,
          align: "center" as const,
          className: "col-self-eval",
          render: (_, record) => record.selfEvaluation.performance,
        },
        {
          title: "공통역량",
          key: "selfCommonCompetency",
          width: 90,
          align: "center" as const,
          className: "col-self-eval",
          render: (_, record) => record.selfEvaluation.commonCompetency,
        },
        {
          title: "역할역량",
          key: "selfRoleCompetency",
          width: 90,
          align: "center" as const,
          className: "col-self-eval",
          render: (_, record) => record.selfEvaluation.roleCompetency,
        },
        {
          title: "직무역량",
          key: "selfJobCompetency",
          width: 90,
          align: "center" as const,
          className: "col-self-eval",
          render: (_, record) => record.selfEvaluation.jobCompetency,
        },
        {
          title: "계",
          key: "selfTotal",
          width: 80,
          align: "center" as const,
          className: "col-self-eval",
          sorter: (a, b) =>
            a.selfEvaluation.total - b.selfEvaluation.total,
          render: (_, record) => (
            <strong style={{ color: "#52c41a" }}>
              {record.selfEvaluation.total}
            </strong>
          ),
        },
      ],
    },
    {
      title: "프로젝트평가",
      className: "col-project-eval",
      children: [
        {
          title: "프로젝트명",
          key: "projectName",
          width: 150,
          align: "center" as const,
          className: "col-project-eval",
          render: (_, record) => (
            <div>
              {record.projectEvaluations.map((p, idx) => (
                <div
                  key={p.key}
                  style={{
                    height: "64px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderBottom:
                      idx < record.projectEvaluations.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                    fontWeight: "500",
                  }}
                >
                  {p.projectName}
                </div>
              ))}
            </div>
          ),
        },
        {
          title: "공통",
          key: "projectCommon",
          width: 80,
          align: "center" as const,
          className: "col-project-eval",
          render: (_, record) => (
            <div>
              {record.projectEvaluations.map((p, idx) => (
                <div
                  key={p.key}
                  style={{
                    height: "64px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderBottom:
                      idx < record.projectEvaluations.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                  }}
                >
                  {p.common}
                </div>
              ))}
            </div>
          ),
        },
        {
          title: "직무별",
          key: "projectJobSpecific",
          width: 80,
          align: "center" as const,
          className: "col-project-eval",
          render: (_, record) => (
            <div>
              {record.projectEvaluations.map((p, idx) => (
                <div
                  key={p.key}
                  style={{
                    height: "64px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderBottom:
                      idx < record.projectEvaluations.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                  }}
                >
                  {p.jobSpecific}
                </div>
              ))}
            </div>
          ),
        },
        {
          title: "계",
          key: "projectTotal",
          width: 80,
          align: "center" as const,
          className: "col-project-eval",
          render: (_, record) => (
            <div>
              {record.projectEvaluations.map((p, idx) => (
                <div
                  key={p.key}
                  style={{
                    height: "64px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderBottom:
                      idx < record.projectEvaluations.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                  }}
                >
                  <strong style={{ color: "#722ed1" }}>
                    {p.total}
                  </strong>
                </div>
              ))}
            </div>
          ),
        },
        {
          title: "화면",
          key: "projectView",
          width: 100,
          align: "center" as const,
          className: "col-project-eval",
          render: (_, record) => (
            <div>
              {record.projectEvaluations.map((p, idx) => (
                <div
                  key={p.key}
                  style={{
                    height: "64px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderBottom:
                      idx < record.projectEvaluations.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                  }}
                >
                  <Button
                    type="link"
                    size="small"
                    onClick={() => p.pmSubYn === 'Y' && openProjectEvalModal(record, p)}
                    style={{ 
                      color: p.pmSubYn === 'Y' ? undefined : "#999", 
                      cursor: p.pmSubYn === 'Y' ? "pointer" : "default" 
                    }}
                  >
                    {p.evlCd === '03' ? 'PM평가' : '팀원평가'}
                  </Button>
                </div>
              ))}
            </div>
          ),
        },
      ],
    },
    {
      title: "1차평가",
      className: "col-first-eval",
      children: [
        {
          title: "업적평가",
          key: "firstPerformance",
          width: 90,
          align: "center" as const,
          className: "col-first-eval",
          render: (_, record) => record.firstEvaluation.performance,
        },
        {
          title: "공통역량",
          key: "firstCommonCompetency",
          width: 90,
          align: "center" as const,
          className: "col-first-eval",
          render: (_, record) => record.firstEvaluation.commonCompetency,
        },
        {
          title: "역할역량",
          key: "firstRoleCompetency",
          width: 90,
          align: "center" as const,
          className: "col-first-eval",
          render: (_, record) => record.firstEvaluation.roleCompetency,
        },
        {
          title: "직무역량",
          key: "firstJobCompetency",
          width: 90,
          align: "center" as const,
          className: "col-first-eval",
          render: (_, record) => record.firstEvaluation.jobCompetency,
        },
        {
          title: "계",
          key: "firstTotal",
          width: 80,
          align: "center" as const,
          className: "col-first-eval",
          sorter: (a, b) =>
            a.firstEvaluation.total - b.firstEvaluation.total,
          render: (_, record) => (
            <strong style={{ color: "#1890ff" }}>
              {record.firstEvaluation.total}
            </strong>
          ),
        },
      ],
    },
    {
      title: "2차평가",
      className: "col-second-eval",
      children: [
        {
          title: "업적평가",
          key: "secondPerformance",
          width: 90,
          align: "center" as const,
          className: "col-second-eval",
          render: (_, record) => {
            // 관리자 또는 2차평가자/임원(canViewSecondEval)만 표시
            const canView = isHrAdmin || record.canViewSecondEval === 'Y';
            return canView ? record.secondEvaluation.performance : '-';
          },
        },
        {
          title: "공통역량",
          key: "secondCommonCompetency",
          width: 90,
          align: "center" as const,
          className: "col-second-eval",
          render: (_, record) => {
            const canView = isHrAdmin || record.canViewSecondEval === 'Y';
            return canView ? record.secondEvaluation.commonCompetency : '-';
          },
        },
        {
          title: "역할역량",
          key: "secondRoleCompetency",
          width: 90,
          align: "center" as const,
          className: "col-second-eval",
          render: (_, record) => {
            const canView = isHrAdmin || record.canViewSecondEval === 'Y';
            return canView ? record.secondEvaluation.roleCompetency : '-';
          },
        },
        {
          title: "직무역량",
          key: "secondJobCompetency",
          width: 90,
          align: "center" as const,
          className: "col-second-eval",
          render: (_, record) => {
            const canView = isHrAdmin || record.canViewSecondEval === 'Y';
            return canView ? record.secondEvaluation.jobCompetency : '-';
          },
        },
        {
          title: "계",
          key: "secondTotal",
          width: 80,
          align: "center" as const,
          className: "col-second-eval",
          sorter: (a, b) =>
            (a.secondEvaluation.total || 0) - (b.secondEvaluation.total || 0),
          render: (_, record) => {
            const canView = isHrAdmin || record.canViewSecondEval === 'Y';
            if (!canView) return (
              <strong style={{ color: "#999" }}>-</strong>
            );
            return (
              <strong style={{ color: "#fa8c16" }}>
                {record.secondEvaluation.total}
              </strong>
            );
          },
        },
      ],
    },
  ];

  return (
    <>
      {contextHolder}
      <div
        style={{
          backgroundColor: "#ffffff",
          minHeight: "100vh",
        }}
      >
        {/* 페이지 제목 */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", position: "relative", padding: "24px 0 20px 0" }}>
          {isHrAdmin && period === 'HR' && evaluationData.length > 0 && (
            <span style={{ position: "absolute", left: 24, fontSize: "16px", fontWeight: 600, color: "#fa8c16" }}>
              최종점수 수정 기한: {form.getFieldValue('evaluationYear') || ''}-12-31
            </span>
          )}
          <Title level={2} style={{ margin: 0 }}>
            최종 평가 결과
          </Title>
        </div>

      {/* 점수별 현황 대시보드 - HR 기간 + 관리자만 표시 */}
      {period === 'HR' && isHrAdmin && evaluationData.length > 0 && (
      <div style={{ 
        marginBottom: "0",
        backgroundColor: "#fff",
        padding: "20px 24px",
        borderBottom: "1px solid #f0f0f0"
      }}>
        <div style={{ 
          marginBottom: "16px", 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center" 
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Title level={5} style={{ margin: 0 }}>
              점수별 현황 대시보드
            </Title>
            <Tag color="blue" style={{ fontSize: "11px", margin: 0 }}>
              {totalCount < evaluationData.length 
                ? `${totalCount}명 / 전체 ${evaluationData.length}명`
                : `전체 ${totalCount}명`}
            </Tag>
          </div>
          {selectedGrades.length > 0 && (
            <Button size="small" type="dashed" onClick={() => setSelectedGrades([])}>
              필터 초기화
            </Button>
          )}
        </div>
        
        <div style={{ 
          marginBottom: "16px", 
          padding: "10px 14px", 
          background: "linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)", 
          border: "1px solid #adc6ff",
          borderRadius: "8px"
        }}>
          <div style={{ fontSize: "12px", fontWeight: "600", marginBottom: "2px", color: "#0050b3" }}>
            개인 부여 점수 통계 (최종점수 기준)
          </div>
          <div style={{ fontSize: "12px", color: "#595959" }}>
            2차 평가 제출 완료된 인원의 최종점수 분포를 확인할 수 있습니다. 카드를 클릭하면 해당 구간으로 필터링됩니다.
          </div>
        </div>

        {selectedGrades.length > 0 && (
          <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
            <Tag color="blue" style={{ fontSize: "11px" }}>필터 적용 중:</Tag>
            {selectedGrades.map(key => {
              const rangeData = scoreRangeStats[key as keyof typeof scoreRangeStats];
              return rangeData ? (
                <Tag 
                  key={key} 
                  color={rangeData.color} 
                  closable 
                  onClose={() => handleGradeClick(key)} 
                  style={{ fontSize: "11px" }}
                >
                  {rangeData.label}
                </Tag>
              ) : null;
            })}
          </div>
        )}
        
        <Row gutter={[16, 16]} justify="start">
          {Object.entries(scoreRangeStats).map(([key, rangeData]) => {
            const percentage = totalCount > 0 ? (rangeData.count / totalCount) * 100 : 0;
            const isSelected = selectedGrades.includes(key);
            const evaluatedCount = totalCount - scoreRangeStats.notEvaluated.count;
            const isNotEval = key === 'notEvaluated';
            
            return (
              <Col key={key} xs={24} sm={12} md={8} lg={4} xl={4}>
                <div
                  style={{
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: isSelected ? rangeData.color + "08" : "#fff",
                    cursor: "pointer",
                    border: isSelected ? `2px solid ${rangeData.color}` : "1px solid #e8e8e8",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    padding: "16px",
                    borderRadius: "12px",
                    boxShadow: isSelected ? `0 4px 12px ${rangeData.color}30` : "0 1px 4px rgba(0,0,0,0.06)",
                  }}
                  onClick={() => handleGradeClick(key)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = `0 6px 16px ${rangeData.color}25`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = isSelected ? `0 4px 12px ${rangeData.color}30` : "0 1px 4px rgba(0,0,0,0.06)";
                  }}
                >
                  {/* 상단 라벨 */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <span style={{ fontSize: "13px", fontWeight: 600, color: rangeData.color }}>
                      {rangeData.icon} {rangeData.label}
                    </span>
                    {isSelected && (
                      <Tag color={rangeData.color} style={{ fontSize: "9px", margin: 0, lineHeight: "16px" }}>
                        선택됨
                      </Tag>
                    )}
                  </div>

                  {/* 인원수 */}
                  <div style={{ marginBottom: "12px" }}>
                    <span style={{ fontSize: "28px", fontWeight: "bold", color: rangeData.color, lineHeight: 1 }}>
                      {rangeData.count}
                    </span>
                    <span style={{ fontSize: "14px", color: "#8c8c8c", marginLeft: "2px" }}>
                      /{totalCount}명
                    </span>
                  </div>

                  {/* 프로그레스 바 */}
                  <div style={{ 
                    width: "100%", 
                    height: "6px", 
                    backgroundColor: "#f0f0f0", 
                    borderRadius: "3px", 
                    overflow: "hidden",
                    marginBottom: "8px" 
                  }}>
                    <div style={{ 
                      width: `${Math.round(percentage)}%`, 
                      height: "100%", 
                      background: rangeData.gradient, 
                      borderRadius: "3px",
                      transition: "width 0.6s ease" 
                    }} />
                  </div>

                  {/* 비율 */}
                  <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
                    <span>비율: </span>
                    <span style={{ fontWeight: 600, color: rangeData.color }}>
                      {percentage.toFixed(1)}%
                    </span>
                    {!isNotEval && evaluatedCount > 0 && (
                      <span style={{ marginLeft: "8px", fontSize: "11px", color: "#bfbfbf" }}>
                        (평가완료 중 {((rangeData.count / evaluatedCount) * 100).toFixed(1)}%)
                      </span>
                    )}
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
      )}

      {/* 검색 필터 및 결과 테이블 */}
      <div style={{ backgroundColor: "#fff" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            padding: "20px 20px 0 20px",
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            검색 필터
          </Title>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {period === 'HR' && evaluationData.length > 0 && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="middle"
              onClick={handleExcelDownload}
              style={{
                backgroundColor: "#FFFFFF",
                borderColor: "#52c41a",
                color: "#389e0d",
              }}
            >
              엑셀 다운로드
            </Button>
          )}
          </div>
        </div>

        {/* 검색 필터 */}
        <Form
          form={form}
          layout="inline"
          onFinish={handleSearch}
          initialValues={{ department: "", position: "" }}
          style={{ marginBottom: "20px", padding: "0 20px", display: "flex", gap: "16px", flexWrap: "nowrap" }}
        >
          <Form.Item name="evaluationYear" label="평가년도" style={{ marginBottom: 0 }}>
            <Select
              placeholder="년도 선택"
              style={{ width: 100 }}
              allowClear
              onChange={handleYearChange}
            >
              {evaluationYears.map((item: any) => (
                <Option key={item.evlYear} value={item.evlYear}>{item.evlYear}년</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="evaluateeName" label="피평가자명" style={{ marginBottom: 0 }}>
            <Input
              placeholder="이름 입력"
              style={{ width: 120 }}
            />
          </Form.Item>
          <Form.Item name="department" label="부서" style={{ marginBottom: 0 }}>
            <Select
              placeholder="부서 선택"
              style={{ width: 170 }}
              allowClear
            >
              <Option value="">전체</Option>
              {Array.from(new Set(evaluationData.map(item => item.department))).filter(Boolean).sort((a, b) => a.localeCompare(b, 'ko')).map(dept => (
                <Option key={dept} value={dept}>{dept}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="position" label="직급" style={{ marginBottom: 0 }}>
            <Select
              placeholder="직급 선택"
              style={{ width: 120 }}
              allowClear
            >
              <Option value="">전체</Option>
              {(() => {
                const posOrder: { [key: string]: number } = { '상무이사': 1, '이사': 2, '부장': 3, '차장': 4, '과장': 5, '주임': 6, '사원': 7 };
                return Array.from(new Set(evaluationData.map(item => item.position))).filter(Boolean)
                  .sort((a, b) => (posOrder[a] ?? 99) - (posOrder[b] ?? 99))
                  .map(pos => (
                    <Option key={pos} value={pos}>{pos}</Option>
                  ));
              })()}
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <button
                type="submit"
                style={{
                  padding: "4px 15px",
                  backgroundColor: "#1890ff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                검색
              </button>
              <button
                type="button"
                onClick={handleReset}
                style={{
                  padding: "4px 15px",
                  backgroundColor: "white",
                  color: "#000",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                초기화
              </button>
            </Space>
          </Form.Item>
        </Form>
        
        {/* 메인 테이블 */}
        <style>
          {`
            /* className 기반 헤더 스타일 */
            
            /* 자기평가 헤더 - 연두색 */
            .ant-table-thead th.col-self-eval {
              background-color: #b7eb8f !important;
              font-weight: bold !important;
            }
            
            /* 프로젝트평가 헤더 - 보라색 */
            .ant-table-thead th.col-project-eval {
              background-color: #d3adf7 !important;
              font-weight: bold !important;
            }
            
            /* 1차평가 헤더 - 파란색 */
            .ant-table-thead th.col-first-eval {
              background-color: #91d5ff !important;
              font-weight: bold !important;
            }
            
            /* 2차평가 헤더 - 주황색 */
            .ant-table-thead th.col-second-eval {
              background-color: #ffd591 !important;
              font-weight: bold !important;
            }
            
            /* sorter 아이콘이 있어도 텍스트 가운데 정렬 유지 */
            .ant-table-thead th .ant-table-column-sorters {
              justify-content: center;
            }
          `}
        </style>
        <div style={{ padding: "0 20px 20px 20px" }}>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="key"
            scroll={{ x: 2800 }}
            bordered
            loading={loading}
            onChange={(_pagination, _filters, sorter) => {
              setSortedInfo(sorter as SorterResult<EvaluationRecord>);
            }}
            pagination={{
              current: currentPage,
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => `총 ${total}명`,
              onChange: (page) => setCurrentPage(page),
            }}
            size="small"
          />
        </div>
      </div>
      
      {/* 최종점수 수정 모달 */}
      <Modal
        title={
          <div style={{ fontSize: "16px", fontWeight: "bold" }}>
            최종점수 수정
          </div>
        }
        open={isModalOpen}
        onCancel={closeScoreModal}
        destroyOnClose
        width={500}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Button onClick={resetScoreModal}>초기화</Button>
            <div>
              <Button onClick={closeScoreModal} style={{ marginRight: 8 }}>취소</Button>
              <Button type="primary" onClick={handleSaveScore}>저장</Button>
            </div>
          </div>
        }
      >
        {editingRecord && (
          <div>
            <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "6px" }}>
              <div style={{ marginBottom: "4px", fontSize: "13px" }}>
                <strong>피평가자:</strong> {editingRecord.evaluateeName} ({editingRecord.position}) | <strong>부서:</strong> {editingRecord.department} | <strong>년도:</strong> {editingRecord.evaluationYear}
              </div>
            </div>
            
            <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: "6px", fontSize: "13px", fontWeight: "bold" }}>현재 점수</div>
                <Input
                  value={editingRecord.finalScore}
                  disabled
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    textAlign: "center",
                    backgroundColor: "#f0f0f0",
                  }}
                />
              </div>
              
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: "6px", fontSize: "13px", fontWeight: "bold" }}>
                  새로운 점수 <span style={{ color: "red" }}>*</span>
                </div>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={newScore}
                  onChange={(e) => {
                    const val = e.target.value;
                    // 빈값 허용 (지우는 중)
                    if (val === "") {
                      setNewScore("");
                      setIsResetMode(false);
                      return;
                    }
                    // 숫자와 소수점만 허용, 소수점 2자리까지만
                    if (/^\d{0,3}(\.\d{0,2})?$/.test(val)) {
                      setNewScore(val);
                      setIsResetMode(false);
                    }
                    // 매칭 안 되면 입력 무시 (이전 값 유지)
                  }}
                  onBlur={() => {
                    // 빈값이면 0으로
                    if (newScore === "" || newScore === ".") {
                      setNewScore("0");
                    }
                  }}
                  placeholder="점수 입력"
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                />
                <div style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>
                  (0 ~ 100.00 범위, 소수점 2자리까지 입력 가능)
                </div>
              </div>
            </div>
            
            <div style={{ marginBottom: "16px" }}>
              <div style={{ marginBottom: "6px", fontSize: "13px", fontWeight: "bold" }}>
                수정 사유 <span style={{ color: "red" }}>*</span>
              </div>
              <div style={{ position: 'relative' }}>
                <Input.TextArea
                  key={editingRecord.empNo + editingRecord.evaluationYear + resetKey}
                  defaultValue={scoreReason}
                  onChange={(e) => {
                    scoreReasonRef.current = e.target.value;
                    setScoreReasonCount(e.target.value.length);
                    if (e.target.value.trim()) setIsResetMode(false);
                  }}
                  placeholder="점수 수정 사유를 입력하세요"
                  rows={3}
                  maxLength={500}
                  style={{ paddingBottom: 22 }}
                />
                <span style={{
                  position: 'absolute',
                  right: 12,
                  bottom: 6,
                  fontSize: 11,
                  color: '#bbb',
                  pointerEvents: 'none',
                  lineHeight: 1,
                }}>
                  {scoreReasonCount} / 500
                </span>
              </div>
            </div>
            
            {editingRecord.finalScoreHistory.length > 0 && (
              <div>
                <div style={{ marginBottom: "8px", fontSize: "13px", fontWeight: "bold" }}>수정 이력</div>
                <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: "6px", padding: "8px" }}>
                  <Timeline
                    items={editingRecord.finalScoreHistory.map((history) => ({
                      color: "blue",
                      children: (
                        <div style={{ fontSize: "12px" }}>
                          <div style={{ fontWeight: "bold" }}>
                            {history.oldScore} → {history.newScore} 점
                          </div>
                          <div style={{ color: "#8c8c8c", fontSize: "11px" }}>
                            {history.date} | {history.editor}
                          </div>
                          <div style={{ marginTop: "2px", fontSize: "12px" }}>{history.reason}</div>
                        </div>
                      ),
                    }))}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 업무서약서 조회 모달 */}
      <Modal
        title="업무서약서 조회"
        open={isEvl0102mOpen}
        onCancel={() => { setIsEvl0102mOpen(false); setSelectedEvl0102mRecord(null); }}
        destroyOnClose
        width={1400}
        styles={{ body: { height: '800px', overflowY: 'auto' } }}
        footer={null}
      >
        {selectedEvl0102mRecord && (
          <>
            <Evl0102m
              empNo={selectedEvl0102mRecord.empNo}
              evlYear={selectedEvl0102mRecord.evaluationYear}
              viewMode="readonly"
            />
          </>
        )}
      </Modal>

      {/* 인사평가(HR) 조회 모달 */}
      <Modal
        title="인사평가 조회"
        open={isEvl0105mOpen}
        onCancel={() => { setIsEvl0105mOpen(false); setSelectedEvl0105mRecord(null); }}
        destroyOnClose
        width={1400}
        styles={{ body: { height: '800px', overflowY: 'auto' } }}
        footer={null}
      >
        {selectedEvl0105mRecord && (
          <>
            <Evl0105m
              empNo={common.getEmpNo()}
              hrMapId={selectedEvl0105mRecord.hrMapId}
              evlYear={selectedEvl0105mRecord.evaluationYear}
              evlType={(positionOrder[selectedEvl0105mRecord.position] || 0) <= 2 ? "02" : "01"}
              hrEvlItmId=""
              evlEnableYn="N"
              evlEditableYn="N"
              viewMode="readonly"
            />
          </>
        )}
      </Modal>

      {/* 프로젝트평가(팀원) 조회 모달 - evl0106m */}
      <Modal
        title={`프로젝트 팀원평가 조회 - ${selectedEvl0106mRecord?.record.evaluateeName || ''} (${selectedEvl0106mRecord?.project.projectName || ''})`}
        open={isEvl0106mOpen}
        onCancel={() => { setIsEvl0106mOpen(false); setSelectedEvl0106mRecord(null); }}
        destroyOnClose
        width={1400}
        styles={{ body: { height: '800px', overflowY: 'auto' } }}
        footer={null}
      >
        {selectedEvl0106mRecord && (
          <Evl0106m
            key={`${selectedEvl0106mRecord.evlId}_${selectedEvl0106mRecord.empNo}`}
            userNm={selectedEvl0106mRecord.record.evaluateeName}
            dept={selectedEvl0106mRecord.record.department}
            pm=""
            pjNm={selectedEvl0106mRecord.project.projectName}
            subSys=""
            jobRole="PM"
            evlId={selectedEvl0106mRecord.evlId}
            empNo={selectedEvl0106mRecord.empNo}
            evlEnableYn="N"
            viewMode="readonly"
          />
        )}
      </Modal>

      {/* 프로젝트평가(PM) 조회 모달 - evl0107m */}
      <Modal
        title={`프로젝트 PM평가 조회 - ${selectedEvl0107mRecord?.record.evaluateeName || ''} (${selectedEvl0107mRecord?.project.projectName || ''})`}
        open={isEvl0107mOpen}
        onCancel={() => { setIsEvl0107mOpen(false); setSelectedEvl0107mRecord(null); }}
        destroyOnClose
        width={1400}
        styles={{ body: { height: '800px', overflowY: 'auto' } }}
        footer={null}
      >
        {selectedEvl0107mRecord && (
          <Evl0107m key={`${selectedEvl0107mRecord.evlId}_${selectedEvl0107mRecord.empNo}`} viewMode="readonly" />
        )}
      </Modal>

      </div>
    </>
  );
};

export default Evl0103m;